import { useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { genId } from '../utils/compose';
import { writeDraftMirror, removeDraftMirror } from '../services/draftMirror';

export interface DraftSnapshot {
  accountId: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  bodyLang: string;
  targetLang: string;
}

const LS_PREFIX = 'mf_draft_';

/**
 * Auto-saves the email being composed so nothing is ever lost — even on a crash,
 * network drop, or accidental close.
 *
 *  - Writes to localStorage on every tick (works offline / survives crashes)
 *  - Debounced save to the server Drafts folder (so it shows in the Drafts list)
 *  - Saves on tab hide / page unload
 *  - `clearDraft()` removes it everywhere (call after a successful send or explicit discard)
 *
 * Closing the composer normally KEEPS the draft (it stays in the Drafts folder).
 */
export function useAutoSaveDraft(opts: {
  getSnapshot: () => DraftSnapshot;
  initialDraftId?: string | null;
  active: boolean; // set false to stop auto-saving (during/after send or discard)
}) {
  const draftIdRef = useRef<string | null>(opts.initialDraftId || null);
  const localKeyRef = useRef<string>(LS_PREFIX + (opts.initialDraftId || genId()));
  const lastSavedRef = useRef<string>('');
  const savingRef = useRef(false);

  const getSnapshotRef = useRef(opts.getSnapshot);
  getSnapshotRef.current = opts.getSnapshot;
  const activeRef = useRef(opts.active);
  activeRef.current = opts.active;

  const hasContent = (s: DraftSnapshot) =>
    !!(
      s.bodyHtml.replace(/<[^>]+>/g, '').trim() ||
      s.bodyHtml.includes('<img') ||
      s.subject.trim() ||
      s.to.length ||
      s.cc.length
    );

  const persist = async () => {
    if (!activeRef.current || savingRef.current) return;
    const snap = getSnapshotRef.current();
    if (!snap.accountId || !hasContent(snap)) return;

    const serialized = JSON.stringify(snap);

    // 1) Mirror to localStorage first — synchronous, offline-safe. Bounded and swept by
    //    services/draftMirror (incident 2026-09-23: unbounded mirrors filled the origin quota).
    writeDraftMirror(localKeyRef.current, JSON.stringify({ ...snap, draftId: draftIdRef.current, ts: Date.now() }));

    // 2) Save to server only when something actually changed.
    if (serialized === lastSavedRef.current) return;
    savingRef.current = true;
    try {
      const res = await api.saveDraft({ id: draftIdRef.current, ...snap });
      if (res?.id) {
        draftIdRef.current = res.id;
        const next = LS_PREFIX + res.id;
        if (next !== localKeyRef.current) removeDraftMirror(localKeyRef.current); // the pre-id temp key was an orphan forever
        localKeyRef.current = next;
      }
      lastSavedRef.current = serialized;
    } catch {
      /* offline — localStorage already holds it; will retry on next tick */
    } finally {
      savingRef.current = false;
    }
  };

  useEffect(() => {
    if (!opts.active) return;
    const initial = setTimeout(persist, 1500);   // first save shortly after open
    const interval = setInterval(persist, 3000); // then every 3s if changed
    const onHide = () => { persist(); };
    window.addEventListener('beforeunload', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      clearTimeout(initial);
      clearInterval(interval);
      window.removeEventListener('beforeunload', onHide);
      document.removeEventListener('visibilitychange', onHide);
      persist(); // final flush — keeps the draft when the composer closes
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active]);

  const clearDraft = async () => {
    activeRef.current = false;
    removeDraftMirror(localKeyRef.current);
    if (draftIdRef.current) {
      await api.deleteDraft(draftIdRef.current).catch(() => {});
    }
  };

  return { draftIdRef, clearDraft, saveNow: persist };
}
